// Components==============
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import updateTraining from 'actions/training/training/update';
import CircleButton from 'components/UI/CircleButton/CircleButton';
import DreamSelectModal from 'components/UI/DreamSelectModal/DreamSelectModal';
import { AnimatePresence, motion } from 'framer-motion';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { framerCollapse, framerFade } from 'utilities/FramerAnimations';
import { GlobalTrainingUserContext } from 'utilities/GlobalContext/GlobalTrainingUser';
import { TrainingDetailsContext } from '../../TrainingDetails';
import { getTrainingIsCompleted } from '../getTrainingIsCompleted';
import './Menu.module.scss';
// =========================

export default function Menu() {
  const translate = useTranslate();
  const { training, sessionId, subscriber } = useContext(
    TrainingDetailsContext
  );

  const dispatch = useReduxAction();

  const { setShouldResetTrainingUser } = useContext(GlobalTrainingUserContext);

  const history = useHistory();

  const [displayOverflow, setDisplayOverflow] = useState(false);

  useEffect(() => {
    setDisplayOverflow(false);

    const timer = setTimeout(() => {
      setDisplayOverflow(true);
    }, 250);

    return () => clearTimeout(timer);
  }, []);

  if (!training?.sessions?.length || !sessionId) return <></>;

  const sessionIdList = training.sessions
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
    .map((session) => session.id);

  const currentIdIndex = sessionIdList.indexOf(sessionId);

  function handlePrevious() {
    setShouldResetTrainingUser(false);
    history.push(
      `/werkplaats/training-details/${training?.id}/${
        sessionIdList[currentIdIndex - 1]
      }`
    );
  }

  function handleNext() {
    setShouldResetTrainingUser(false);
    history.push(
      `/werkplaats/training-details/${training?.id}/${
        sessionIdList[currentIdIndex + 1]
      }`
    );
  }

  function handleContributeDream(dreams: number[]) {
    dispatch(
      updateTraining({
        trainingId: training?.id,
        synchDreams: 1,
        dreams,
      })
    );
  }

  const completedTraining =
    training.sessions.find((session) => {
      const completedLessons = session.lessons?.filter((lesson) => {
        const attendance = subscriber?.attendances?.find(
          (a) => a.lesson_id === lesson.id
        );

        return getTrainingIsCompleted(lesson, attendance, subscriber?.user_id);
      });

      return completedLessons?.length !== session.lessons?.length;
    }) === undefined;

  return (
    <div styleName="wrapper">
      <div
        styleName="sessions"
        style={{ overflow: displayOverflow ? 'auto' : 'hidden' }}
      >
        {training.sessions
          .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
          .map((session) => {
            const completedLessons = session.lessons?.filter((lesson) => {
              const attendance = subscriber?.attendances?.find(
                (a) => a.lesson_id === lesson.id
              );

              return getTrainingIsCompleted(
                lesson,
                attendance,
                subscriber?.user_id
              );
            });

            return (
              <div
                styleName="session"
                key={session.id}
                style={{
                  boxShadow:
                    session.id === sessionId
                      ? '0 0px 10px rgba(0, 0, 0, 0.05)'
                      : '',
                }}
                onClick={() => {
                  setShouldResetTrainingUser(false);
                  history.push(
                    `/werkplaats/training-details/${training.id}/${session.id}`
                  );
                }}
              >
                <div
                  styleName="top"
                  style={{
                    marginBottom: session.id === sessionId ? '0.5em' : 0,
                  }}
                >
                  <div styleName="title">
                    {!!(
                      completedLessons?.length === session.lessons?.length
                    ) && <FontAwesomeIcon icon={faCheckCircle} />}
                    <p
                      style={{
                        fontWeight: sessionId === session.id ? 600 : 400,
                      }}
                    >
                      {session.title}
                    </p>
                  </div>
                  <p styleName="count">
                    {completedLessons?.length}/{session?.lessons?.length}
                  </p>
                </div>
                <motion.div
                  animate={session.id === sessionId ? 'animate' : 'initial'}
                  initial="initial"
                  // @ts-ignore
                  variants={framerCollapse}
                  styleName="lessons"
                >
                  {session.lessons
                    ?.sort(
                      (a, b) => (a.order_index || 0) - (b.order_index || 0)
                    )
                    .map((lesson, index) => {
                      const attendance = subscriber?.attendances?.find(
                        (a) => a.lesson_id === lesson.id
                      );

                      const completed = getTrainingIsCompleted(
                        lesson,
                        attendance,
                        subscriber?.user_id
                      );

                      const lessonTitle = `${translate(
                        'course.terms.lesson'
                      )} ${index + 1}`;

                      return (
                        <a key={index} href={`#${lessonTitle}`}>
                          <div
                            styleName={`lesson ${completed ? 'completed' : ''}`}
                          >
                            <p>{lesson.title}</p>

                            <AnimatePresence>
                              {completed && (
                                <motion.div {...framerFade}>
                                  <FontAwesomeIcon icon={faCheck} />
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </a>
                      );
                    })}
                </motion.div>
              </div>
            );
          })}
      </div>
      <div styleName="button-wrapper">
        {completedTraining && (
          <DreamSelectModal
            white
            selected={training.dreams?.map((d) => d.id) || []}
            handleSubmit={handleContributeDream}
          />
        )}
        <CircleButton
          inactive={currentIdIndex === 0}
          onClick={handlePrevious}
          icon={faChevronLeft}
          color="white-blue"
        />
        {completedTraining && currentIdIndex === sessionIdList.length - 1 ? (
          <CircleButton
            to="/werkplaats/mijn-trainingen"
            color="green"
            icon={faCheck}
          />
        ) : (
          <CircleButton
            inactive={currentIdIndex === sessionIdList.length - 1}
            onClick={handleNext}
            icon={faChevronRight}
            color="white-blue"
          />
        )}
      </div>
    </div>
  );
}
